import { memo, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useShop } from '@/contexts';
import { PlayerStatusBar } from '@/components/ui';
import PixelIcon from '@/components/ui/PixelIcon';
import type { GameState } from '@/types/game';
import type { CardInstance } from '@/types/card';
import { getWorldForLevel } from '@/config/worlds';
import { CARD_DEFINITIONS, RARITY_COLORS } from '@/config/cards';
import { PROPS_CATALOG } from '@/types/props';
import { getPropEffectsManager } from '@/game/propEffects';
import {
  FONT_SIZE, SPACING, RADIUS, COLOR, SHADOW, BLUR, Z_INDEX,
  glassPillStyle, actionButtonStyle, resourceChipStyle,
} from '@/config/designTokens';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface MinimalHUDProps {
  gameState: GameState;
  isExpanded: boolean;
  onToggle: () => void;
  onOpenSettings: () => void;
  onRestart: () => void;
  onQuit: () => void;
  onPlayCard?: (cardId: string) => void;
  /** Optional hand override -- when provided, takes precedence over gameState.hand */
  hand?: CardInstance[];
}

// ---------------------------------------------------------------------------
// Card Item (used inside expanded overlay)
// ---------------------------------------------------------------------------

interface CardItemProps {
  card: CardInstance;
  onPlay?: (cardId: string) => void;
}

function CardItem({ card, onPlay }: CardItemProps) {
  const info = CARD_DEFINITIONS[card.type];
  const borderColor = RARITY_COLORS[card.rarity];

  const handlePlay = useCallback(() => {
    onPlay?.(card.id);
  }, [onPlay, card.id]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      style={{
        border: `2px solid ${borderColor}`,
        borderRadius: RADIUS.lg,
        background: 'rgba(0,0,0,0.55)',
        padding: `${SPACING.md} 14px`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 6,
        minWidth: 110,
        maxWidth: 140,
      }}
    >
      <PixelIcon
        name={info.icon as Parameters<typeof PixelIcon>[0]['name']}
        size={28}
        color={borderColor}
      />
      <span
        style={{
          color: COLOR.textWhite,
          fontSize: FONT_SIZE.hudPill,
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {info.name}
      </span>
      <span
        style={{
          color: 'rgba(255,255,255,0.65)',
          fontSize: FONT_SIZE.buttonSmall,
          textAlign: 'center',
          lineHeight: 1.3,
        }}
      >
        {info.description}
      </span>
      <span
        style={{
          color: borderColor,
          fontSize: FONT_SIZE.caption,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
        }}
      >
        {card.rarity}
      </span>

      {onPlay && (
        <motion.button
          type="button"
          whileTap={{ scale: 0.92 }}
          onClick={handlePlay}
          style={{
            marginTop: SPACING.xs,
            padding: `5px ${SPACING.lg}`,
            borderRadius: RADIUS.sm,
            border: `1.5px solid ${borderColor}`,
            background: COLOR.surfaceHover,
            color: COLOR.textWhite,
            fontSize: FONT_SIZE.body,
            fontWeight: 600,
            cursor: 'pointer',
            letterSpacing: '0.04em',
          }}
        >
          Play
        </motion.button>
      )}
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Expanded Overlay
// ---------------------------------------------------------------------------

interface ExpandedOverlayProps {
  gameState: GameState;
  onToggle: () => void;
  onRestart: () => void;
  onQuit: () => void;
  onPlayCard?: (cardId: string) => void;
  hand?: CardInstance[];
}

function ExpandedOverlay({
  gameState,
  onToggle,
  onRestart,
  onQuit,
  onPlayCard,
  hand,
}: ExpandedOverlayProps) {
  const { shopState, shopManager } = useShop();
  const world = getWorldForLevel(gameState.level ?? 0);
  const streakStatus = shopManager?.getDailyRewardStatus() ?? {
    streak: 0,
    canClaim: false,
  };

  const cards: CardInstance[] = hand ?? gameState.hand ?? [];

  return (
    <motion.div
      key="expanded-overlay"
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: Z_INDEX.pauseOverlay,
        backdropFilter: BLUR.overlay,
        WebkitBackdropFilter: BLUR.overlay,
        background: COLOR.expandedOverlay,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: `${SPACING.xxl} ${SPACING.lg}`,
        overflowY: 'auto',
      }}
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      {/* ---- Top: Player Stats ---- */}
      <div style={{ width: '100%', maxWidth: 480 }}>
        <PlayerStatusBar
          mode="hud"
          worldId={world.id}
          score={gameState.gameStats.score}
          lives={shopState.lives}
          coins={shopState.coins}
          gems={shopState.gems}
          streak={streakStatus.streak}
          canClaimStreak={streakStatus.canClaim}
        />
      </div>

      {/* ---- Middle: Card Hand ---- */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: SPACING.md,
          width: '100%',
          maxWidth: 480,
          padding: `${SPACING.lg} 0`,
        }}
      >
        {cards.length > 0 ? (
          <>
            <span
              style={{
                color: 'rgba(255,255,255,0.55)',
                fontSize: FONT_SIZE.body,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              Hand
            </span>
            <div
              style={{
                display: 'flex',
                gap: SPACING.md,
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}
            >
              <AnimatePresence mode="popLayout">
                {cards.map((card) => (
                  <CardItem key={card.id} card={card} onPlay={onPlayCard} />
                ))}
              </AnimatePresence>
            </div>
          </>
        ) : (
          <span
            style={{
              color: 'rgba(255,255,255,0.35)',
              fontSize: FONT_SIZE.hudPill,
              fontStyle: 'italic',
            }}
          >
            No cards in hand
          </span>
        )}
      </div>

      {/* ---- Bottom: Action Buttons ---- */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: SPACING.md,
          width: '100%',
          maxWidth: 320,
        }}
      >
        <ActionButton
          label="Resume"
          iconName="play"
          iconColor={COLOR.actionResume}
          onClick={onToggle}
        />
        <ActionButton
          label="Restart Level"
          iconName="restart"
          iconColor={COLOR.actionRestart}
          onClick={onRestart}
        />
        <ActionButton
          label="Quit to Map"
          iconName="home"
          iconColor={COLOR.actionQuit}
          onClick={onQuit}
        />
      </div>
    </motion.div>
  );
}

// ---------------------------------------------------------------------------
// Action Button (used inside expanded overlay)
// ---------------------------------------------------------------------------

interface ActionButtonProps {
  label: string;
  iconName: Parameters<typeof PixelIcon>[0]['name'];
  iconColor: string;
  onClick: () => void;
}

function ActionButton({ label, iconName, iconColor, onClick }: ActionButtonProps) {
  return (
    <motion.button
      type="button"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.96 }}
      onClick={onClick}
      style={actionButtonStyle()}
    >
      <PixelIcon name={iconName} size={18} color={iconColor} />
      {label}
    </motion.button>
  );
}

// ---------------------------------------------------------------------------
// MinimalHUD (main export)
// ---------------------------------------------------------------------------

function MinimalHUDComponent({
  gameState,
  isExpanded,
  onToggle,
  onOpenSettings,
  onRestart,
  onQuit,
  onPlayCard,
  hand,
}: MinimalHUDProps) {
  const { shopState, shopManager } = useShop();
  const canClaimDailyReward = shopManager?.getDailyRewardStatus()?.canClaim ?? false;

  const handleSettingsClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onOpenSettings();
    },
    [onOpenSettings],
  );

  // Compute active score multiplier from equipped props
  const scoreMultiplier = useMemo(() => {
    const equippedEffects = (shopState.equippedProps || [])
      .map(id => PROPS_CATALOG.find(p => p.id === id))
      .filter((p): p is NonNullable<typeof p> => !!p)
      .flatMap(p => p.effects || []);
    return getPropEffectsManager().getScoreMultiplier(equippedEffects);
  }, [shopState.equippedProps]);

  const levelLabel = gameState.gameMode === 'endless' && gameState.infiniteState
    ? `W.${gameState.infiniteState.wave}`
    : `Lv.${(gameState.level ?? 0) + 1}`;

  return (
    <>
      {/* ---- Collapsed Pill ---- */}
      {!isExpanded && (
        <motion.div
          layout
          initial={{ opacity: 0, scale: 0.9, x: '-50%' }}
          animate={{ opacity: 1, scale: 1, x: '-50%' }}
          exit={{ opacity: 0, scale: 0.9, x: '-50%' }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          onClick={onToggle}
          role="button"
          tabIndex={0}
          aria-label="Open pause menu"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') onToggle();
          }}
          style={{
            ...glassPillStyle(),
            position: 'fixed',
            top: 5,
            left: '50%',
            zIndex: Z_INDEX.pauseOverlay,
            gap: SPACING.md,
            userSelect: 'none',
          }}
        >
          {/* Lives */}
          <span style={{ ...resourceChipStyle(), gap: 3, fontSize: FONT_SIZE.hudValue, fontWeight: 600 }}>
            <PixelIcon name="life" size={16} color={COLOR.lifeLight} />
            {shopState.lives}
          </span>

          {/* Daily reward unclaimed indicator */}
          {canClaimDailyReward && (
            <motion.div
              style={{
                width: 8,
                height: 8,
                borderRadius: RADIUS.full,
                background: '#f1c40f',
                boxShadow: '0 0 6px rgba(241, 196, 15, 0.8)',
              }}
              animate={{ scale: [1, 1.3, 1], opacity: [0.7, 1, 0.7] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          )}

          {/* DoubleCoins x2 badge */}
          {shopState.powerups.doubleCoins > 0 && (
            <motion.span
              style={{
                color: '#f1c40f',
                fontWeight: 'bold',
                fontSize: FONT_SIZE.caption,
              }}
              initial={{ scale: 0 }}
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
            >
              x2
            </motion.span>
          )}

          {/* Score multiplier badge from props */}
          {scoreMultiplier > 1 && (
            <motion.span
              style={{
                color: COLOR.warningBorder,
                fontWeight: 'bold',
                fontSize: FONT_SIZE.caption,
              }}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
            >
              x{scoreMultiplier.toFixed(1)}
            </motion.span>
          )}

          {/* Level */}
          <span
            style={{
              color: 'rgba(255,255,255,0.8)',
              fontSize: FONT_SIZE.hudPill,
              fontWeight: 500,
            }}
          >
            {levelLabel}
          </span>

          {/* Settings gear */}
          <motion.span
            whileTap={{ scale: 0.85, rotate: -30 }}
            onClick={handleSettingsClick}
            role="button"
            tabIndex={0}
            aria-label="Open settings"
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                onOpenSettings();
              }
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              marginLeft: 2,
            }}
          >
            <PixelIcon name="settings" size={16} color="rgba(255,255,255,0.7)" />
          </motion.span>
        </motion.div>
      )}

      {/* ---- Expanded Overlay ---- */}
      <AnimatePresence>
        {isExpanded && (
          <ExpandedOverlay
            gameState={gameState}
            onToggle={onToggle}
            onRestart={onRestart}
            onQuit={onQuit}
            onPlayCard={onPlayCard}
            hand={hand}
          />
        )}
      </AnimatePresence>
    </>
  );
}

const MinimalHUD = memo(MinimalHUDComponent);
export default MinimalHUD;
